import React from 'react';
import { Card, Table, Row, Col } from 'antd';
import { useOwnerOperations } from '@/hooks/useOwnerIntelligence';
import { HeroKpiCard, OiPageFrame, OiEmptyState, PrimaryKpiCard } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiHorizontalBarChart } from '@/components/owner-intelligence/charts/OiCharts';

type MfgOverview = {
  totalWorkOrders?: number;
  activeWorkOrders?: number;
  completedToday?: number;
  pendingInspection?: number;
  productionRate?: number;
  utilizationRate?: number;
  statusDistribution?: Array<{ status: string; count: number }>;
};

const OperationsIntelligencePage: React.FC = () => {
  const { data, isLoading, error } = useOwnerOperations();
  const mfg = (data?.manufacturing ?? {}) as MfgOverview;
  const statusChart = (mfg.statusDistribution ?? []).map((s) => ({
    name: s.status,
    count: s.count,
  }));

  return (
    <OiPageFrame
      subtitle="Production risk, delayed work orders, and delivery readiness."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Operations intelligence unavailable"
    >
      {data?.on_time_delivery_pct != null ? (
        <Card title="On-time delivery (YTD)" style={{ marginBottom: 16 }} bordered={false}>
          <HeroKpiCard label="OTD %" value={`${data.on_time_delivery_pct}%`} score={data.on_time_delivery_pct} />
        </Card>
      ) : null}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <PrimaryKpiCard label="Total WOs" value={mfg.totalWorkOrders ?? 0} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <PrimaryKpiCard label="Active" value={mfg.activeWorkOrders ?? 0} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <PrimaryKpiCard label="Completed today" value={mfg.completedToday ?? 0} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <PrimaryKpiCard label="Pending inspection" value={mfg.pendingInspection ?? 0} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <PrimaryKpiCard label="Production rate" value={`${mfg.productionRate ?? 0} units/hr`} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <PrimaryKpiCard label="Utilization" value={`${mfg.utilizationRate ?? 0}%`} />
        </Col>
      </Row>

      <Card title="Work order status" style={{ marginBottom: 16 }} bordered={false}>
        <OiHorizontalBarChart data={statusChart} dataKey="count" nameKey="name" height={180} />
        <Table
          size="small"
          rowKey="status"
          pagination={false}
          style={{ marginTop: 16 }}
          dataSource={mfg.statusDistribution ?? []}
          columns={[
            { title: 'Status', dataIndex: 'status' },
            { title: 'Count', dataIndex: 'count' },
          ]}
        />
      </Card>

      <Card title="Delayed deliveries" bordered={false} style={{ marginBottom: 16 }}>
        {(data?.delayed_deliveries ?? []).length === 0 ? (
          <OiEmptyState variant="positive" title="All deliveries on track" body="No delays detected." />
        ) : (
          <Table
            size="small"
            rowKey="id"
            dataSource={data?.delayed_deliveries ?? []}
            columns={[
              { title: 'Delivery', dataIndex: 'delivery_note_number' },
              { title: 'Customer', dataIndex: 'customer_name' },
              { title: 'Status', dataIndex: 'delivery_status' },
            ]}
          />
        )}
      </Card>

      <Card title="Approval load" bordered={false}>
        {!data?.approval_load ? (
          <OiEmptyState title="No approval analytics" body="Approval load metrics will appear when instances exist." />
        ) : (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard label="Pending" value={data.approval_load.pending ?? 0} />
              </Col>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard label="Overdue" value={data.approval_load.overdue ?? 0} />
              </Col>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard label="Escalated" value={data.approval_load.escalated ?? 0} />
              </Col>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard
                  label="Avg approval (min)"
                  value={data.approval_load.avg_approval_minutes ?? 0}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard
                  label="Reject rate (90d)"
                  value={
                    data.approval_load.reject_rate_pct != null
                      ? `${data.approval_load.reject_rate_pct}%`
                      : '—'
                  }
                />
              </Col>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard
                  label="Return rate (90d)"
                  value={
                    data.approval_load.return_rate_pct != null
                      ? `${data.approval_load.return_rate_pct}%`
                      : '—'
                  }
                />
              </Col>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard
                  label="Aging >7d"
                  value={data.approval_load.aging_buckets?.over_7d ?? 0}
                />
              </Col>
              <Col xs={12} sm={6}>
                <PrimaryKpiCard
                  label="Decided (90d)"
                  value={data.approval_load.decided_90d ?? 0}
                />
              </Col>
            </Row>
            {(data.approval_load.aging_buckets || data.approval_load.bottleneck_steps?.length) && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={24} md={12}>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="bucket"
                    title={() => 'Pending aging'}
                    dataSource={[
                      { bucket: '<24h', count: data.approval_load.aging_buckets?.under_24h ?? 0 },
                      { bucket: '1–3d', count: data.approval_load.aging_buckets?.d1_to_3 ?? 0 },
                      { bucket: '3–7d', count: data.approval_load.aging_buckets?.d3_to_7 ?? 0 },
                      { bucket: '>7d', count: data.approval_load.aging_buckets?.over_7d ?? 0 },
                    ]}
                    columns={[
                      { title: 'Bucket', dataIndex: 'bucket' },
                      { title: 'Count', dataIndex: 'count' },
                    ]}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="step_number"
                    title={() => 'Bottleneck steps'}
                    dataSource={data.approval_load.bottleneck_steps ?? []}
                    columns={[
                      { title: 'Step', dataIndex: 'step_number' },
                      { title: 'Pending', dataIndex: 'pending_count' },
                      { title: 'Avg age (h)', dataIndex: 'avg_age_hours' },
                    ]}
                    locale={{ emptyText: 'No pending step bottlenecks' }}
                  />
                </Col>
              </Row>
            )}
            {(data.approval_load.top_risk_categories?.length ?? 0) > 0 && (
              <Table
                size="small"
                pagination={false}
                style={{ marginBottom: 16 }}
                rowKey="category"
                title={() => 'Top AI brief risk categories (cached briefs)'}
                dataSource={data.approval_load.top_risk_categories ?? []}
                columns={[
                  { title: 'Category', dataIndex: 'category' },
                  { title: 'Count', dataIndex: 'count' },
                ]}
              />
            )}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="user_id"
                  title={() => 'Fastest approvers (30d)'}
                  dataSource={data.approval_load.fastest_approvers ?? []}
                  columns={[
                    { title: 'Approver', dataIndex: 'full_name' },
                    { title: 'Avg min', dataIndex: 'avg_minutes' },
                    { title: 'Decisions', dataIndex: 'decisions' },
                  ]}
                />
              </Col>
              <Col xs={24} md={12}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="user_id"
                  title={() => 'Slowest approvers (30d)'}
                  dataSource={data.approval_load.slowest_approvers ?? []}
                  columns={[
                    { title: 'Approver', dataIndex: 'full_name' },
                    { title: 'Avg min', dataIndex: 'avg_minutes' },
                    { title: 'Decisions', dataIndex: 'decisions' },
                  ]}
                />
              </Col>
            </Row>
          </>
        )}
      </Card>
    </OiPageFrame>
  );
};

export default OperationsIntelligencePage;
